import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { addInventoryStatusNote } from '@/services/inventory/inventoryService';

interface Props {
  orderNumber: string;
  status: string;
  onClose: () => void;
  onUpdateStatus: () => Promise<boolean>;
}

export function InventoryStatusNoteDialog({ orderNumber, status, onClose, onUpdateStatus }: Props) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [error, setError] = useState('');
  const busy = useRef(false);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const submit = async () => {
    if (busy.current || !note.trim()) return;
    busy.current = true;
    setSaving(true); setError('');
    const request = new AbortController();
    controller.current = request;
    let saved = noteSaved;
    try {
      if (!saved) {
        await addInventoryStatusNote(orderNumber, note.trim(), request.signal);
        if (request.signal.aborted) return;
        saved = true;
        setNoteSaved(true);
      }
      const updated = await onUpdateStatus();
      if (request.signal.aborted) return;
      if (updated) onClose();
      else setError('The note was saved, but the line status could not be updated. Retry the status update without saving the note again.');
    } catch {
      if (!request.signal.aborted) setError(saved
        ? 'The note was saved, but the line status could not be updated. Retry the status update without saving the note again.'
        : 'Unable to save the note. The line status has not been changed. Please try again.');
    } finally {
      busy.current = false;
      if (!request.signal.aborted) setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" className="inventory-status-note-dialog" disableEscapeKeyDown={saving} onClose={() => { if (!busy.current) onClose(); }} aria-labelledby="inventory-status-note-title">
      <DialogTitle id="inventory-status-note-title">Set Order #{orderNumber} to {status}?</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, mt: 1, lineHeight: 1.75 }}>
          Setting this shipment to {status} will send the order back to the customer service representative. Please add a note to let the representative know why this order needs to be recycled.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          variant="standard"
          fullWidth multiline minRows={1} maxRows={6} autoFocus
          sx={{ mt: 5, mb: 5, '& .MuiInputBase-root': { fontSize: 16, lineHeight: '28px' }, '& .MuiInputBase-input': { paddingBottom: '12px' } }}
          placeholder="Enter notes for this action"
          inputProps={{ 'aria-label': 'Enter notes for this action' }}
          value={note} onChange={(event) => setNote(event.target.value)}
          disabled={saving || noteSaved}
        />
        {noteSaved && <Typography sx={{ mt: 1, fontSize: 14 }}>The note is already saved and will remain on the order even if you cancel.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>CANCEL</Button>
        <LoadingButton variant="contained" disableElevation loading={saving} disabled={!note.trim()} onClick={() => { void submit(); }}>
          {noteSaved && error ? 'RETRY STATUS UPDATE' : `SET TO ${status.toUpperCase()}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}